import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  Menu,
  X,
  User,
  LogOut,
  Check,
  Trash,
  Lock
} from 'lucide-react';
import { getRequest, postRequest } from '../services/apiService'; //

const Navbar = ({ toggleSidebar, sidebarOpen }) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [user, setUser] = useState(null); // State to store user data
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  // Fetch notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await getRequest('notification/notifications/?user_id=1'); //
        if (response.status === 'success') {
          const formattedNotifications = response.data.map(notification => ({
            id: notification.id,
            message: notification.message,
            read: notification.is_read,
            time: formatDateTime(notification.created_at),
          }));
          setNotifications(formattedNotifications);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
  }, []);

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const storedAuthData = localStorage.getItem('authData'); //
        const parsedAuthData = storedAuthData ? JSON.parse(storedAuthData) : null; //
        const userId = parsedAuthData?.user_id; // Assuming user_id is stored in authData

        if (userId) {
          const response = await getRequest(`api/auth/users/?id=${userId}`); //
          
          if (response.results && response.results.status === 'success' && response.results.data && response.results.data.length > 0) { //
            setUser(response.results.data[0]); // Access data inside 'results'
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // Format ISO date to dd/mm/yy hh:mm:ss
  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleLogout = async () => {
    try {
      await postRequest('api/auth/logout'); //
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleNotificationDropdown = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
    setProfileOpen(false);
  };

  const toggleProfileDropdown = () => {
    setProfileOpen(!profileOpen);
    setNotificationDropdownOpen(false);
  };

  const unreadCount = notifications.filter((notif) => !notif.read).length;

  useEffect(() => {
    function handleClickOutside(event) {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        const isNotificationButton = event.target.closest('[data-notification-element="true"]');
        if (!isNotificationButton) setNotificationDropdownOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationDropdownOpen, profileOpen]);

  // Derive initials for the avatar
  const userInitials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : 'AD'; //
  const userFullName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'Admin User'; //
  const userRole = user && user.roles && user.roles.length > 0 ? user.roles[0].name : 'User'; //

  return (
    <div className="bg-white shadow-md w-full">
      <div className="max-w-full mx-auto px-2 sm:px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex-none">
            <button
              onClick={toggleSidebar}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? (
                <X className="block h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <Menu className="block h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </button>
          </div>

          <div className="flex-1 flex justify-center items-center">
            <span className="text-lg sm:text-xl font-bold text-indigo-600 truncate px-2">
              <span className="hidden sm:inline">Project Management System</span>
              <span className="sm:hidden">PMS</span>
            </span>
          </div>

          <div className="flex-none flex items-center space-x-1 sm:space-x-4">
            <div className="relative" ref={notificationRef}>
              <button
                onClick={toggleNotificationDropdown}
                className="relative p-1.5 rounded-full text-gray-600 hover:bg-[#3730a3]/10 hover:text-[#3730a3] focus:outline-none transition-colors duration-200"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 block h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-[#3730a3] text-xs text-white font-bold flex items-center justify-center ring-2 ring-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationDropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-lg shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
                    <div className="flex items-center">
                      <span className="text-xs font-medium text-gray-500">
                        {unreadCount} unread
                      </span>
                    </div>
                  </div>

                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-6 px-4">
                      <div className="bg-[#3730a3]/10 p-3 rounded-full mb-3">
                        <Check className="h-5 w-5 sm:h-6 sm:w-6 text-[#3730a3]" />
                      </div>
                      <p className="text-sm text-gray-500 text-center">No notifications</p>
                    </div>
                  ) : (
                    <div className="max-h-60 sm:max-h-72 overflow-y-auto">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className="px-4 py-3 hover:bg-[#3730a3]/5 transition-colors cursor-default"
                          data-notification-element="true"
                        >
                          <div className="flex items-start">
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium mb-0.5 ${
                                notification.read ? 'text-gray-500' : 'text-gray-800'
                              }`}>
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500">
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative" ref={profileRef}>
              <button
                onClick={toggleProfileDropdown}
                className="flex items-center space-x-1 sm:space-x-2 focus:outline-none"
              >
                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gradient-to-r from-[#3730a3] to-[#3730a3]/80 flex items-center justify-center text-white font-medium text-xs sm:text-sm">
                  {userInitials}
                </div>
                <div className="flex items-center">
                  <span className="hidden sm:inline text-sm font-medium text-gray-700 mr-1">{userRole}</span>
                  <ChevronDown
                    className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                      profileOpen ? 'transform rotate-180' : ''
                    }`}
                  />
                </div>
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800">{userFullName}</p> {/* */}
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p> {/* */}
                  </div>
                  <Link
                    to="/profile"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#3730a3]/5 transition-colors flex items-center"
                  >
                    <User className="h-4 w-4 mr-3 text-[#3730a3]" />
                    Your Profile
                  </Link>
                  <Link
                    to="/profile?tab=changePassword"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#3730a3]/5 transition-colors flex items-center"
                  >
                    <Lock className="h-4 w-4 mr-3 text-[#3730a3]" />
                    Change Password
                  </Link>
                  <div className="border-t border-gray-100 mt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;